import { Link } from "react-router-dom";
import { ShieldCheck, CheckCircle2, ArrowRight } from "lucide-react";

const criteria = [
  { label: "Purchase Pro at least 45 days before your exam date", detail: "So there is a real preparation window between purchase and sitting." },
  { label: "Answer at least 80% of the MCQ bank", detail: "Broad engagement with the material we cover." },
  { label: "Complete all 3 Readiness Trials and at least 5 mock exams", detail: "Timed simulations build exam-day stamina." },
  { label: "Reach a final Readiness Score of 80% or higher on your dashboard", detail: "Your dashboard has to say you are ready before you sit." },
  { label: "Study on at least 60% of the days in your paid period", detail: "Consistency, not cramming." },
  { label: "Submit your claim within 30 days of receiving your official exam result", detail: "Attach your official ADC result letter." },
];

const GuaranteePage = () => (
  <div data-testid="guarantee-page" className="min-h-screen bg-background">
    <div className="max-w-3xl mx-auto px-6 py-16">
      <div className="inline-flex items-center gap-2 rounded-full bg-primary/10 text-primary px-4 py-1.5 text-sm font-semibold mb-6">
        <ShieldCheck className="w-4 h-4" /> The 80% Readiness Commitment
      </div>

      <h1 className="text-4xl sm:text-5xl font-bold tracking-tight mb-4">
        Reach 80% readiness with us. If you still don&apos;t pass, we make it right.
      </h1>
      <p className="text-lg text-muted-foreground mb-10">
        This is our commitment to Pro subscribers who genuinely use the platform to prepare. If your PassADC
        dashboard records at least 80% readiness before your official ADC Part 1 sitting and you do not pass,
        we will extend your Pro access free of charge until your next attempt.
      </p>

      <section className="rounded-2xl border border-primary/30 bg-primary/5 p-6 mb-10" data-testid="guarantee-tier-extension">
        <h2 className="text-lg font-semibold mb-1">What you get</h2>
        <p className="text-sm text-muted-foreground mb-3">If you meet the criteria and your official result is a fail:</p>
        <p className="text-base">We extend your Pro subscription <strong>free of charge until your next ADC Part 1 sitting</strong>, so you can keep preparing with the full toolkit and re-sit without paying again.</p>
      </section>

      <h2 className="text-2xl font-bold mb-4">The 6 criteria (auto-tracked in your dashboard)</h2>
      <ol className="space-y-3 mb-10" data-testid="guarantee-criteria">
        {criteria.map((c, i) => (
          <li key={i} className="flex gap-3 rounded-xl border border-border bg-card p-4">
            <CheckCircle2 className="w-5 h-5 text-primary shrink-0 mt-0.5" />
            <div>
              <div className="font-semibold">{i + 1}. {c.label}</div>
              <div className="text-sm text-muted-foreground mt-0.5">{c.detail}</div>
            </div>
          </li>
        ))}
      </ol>

      <h2 className="text-2xl font-bold mb-3">How to claim</h2>
      <ol className="space-y-2 mb-10 text-base text-muted-foreground list-decimal list-inside">
        <li>Sit your ADC Part 1 exam within your paid period.</li>
        <li>Within 30 days of your result, email <a className="text-primary underline" href="mailto:hello@passadc.com">hello@passadc.com</a> from your PassADC account email with your official ADC result letter attached.</li>
        <li>We check the criteria against your dashboard (all data is already there).</li>
        <li>We reply within 3 business days with your free Pro extension applied.</li>
      </ol>

      <h2 className="text-2xl font-bold mb-3">Fair-use notes</h2>
      <ul className="space-y-2 mb-10 text-sm text-muted-foreground list-disc list-inside">
        <li>One claim per account per exam sitting.</li>
        <li>Fraudulent claims (e.g. accounts sharing answers to hit coverage) will be denied and the account closed.</li>
        <li>We can update these criteria for new customers, but your criteria are locked to what was published on the date you purchased.</li>
        <li>This commitment covers all paid plans — both Pro Monthly and the March 2027 Pass.</li>
        <li>This commitment is in addition to your rights under the Australian Consumer Law.</li>
        <li>PassADC is an independent preparation platform and is not affiliated with the Australian Dental Council.</li>
      </ul>

      <div className="rounded-2xl border border-border bg-card p-6 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h3 className="text-lg font-semibold">Ready to start with the guarantee?</h3>
          <p className="text-sm text-muted-foreground">You&apos;re already tracked from day one.</p>
        </div>
        <Link to="/billing" data-testid="guarantee-cta-billing"
              className="inline-flex items-center gap-2 rounded-full bg-primary text-primary-foreground px-5 py-2.5 font-semibold hover:opacity-90">
          See Pro plans <ArrowRight className="w-4 h-4" />
        </Link>
      </div>
    </div>
  </div>
);

export default GuaranteePage;
